import { createTheme } from "@mui/material";
import { TABLE_TOKENS } from "./styles";

type TableThemeOptions = {
  headerColor?: string;
  /** Orders list filter popover. Other tables keep the shared filter chrome. */
  brandFilter?: boolean;
};

/**
 * Shared MUI theme overrides for mui-datatables.
 * Wrap each MUIDataTable with: <ThemeProvider theme={getTableTheme()}>
 */
export const getTableTheme = (options?: TableThemeOptions) => {
  const headerColor = options?.headerColor ?? TABLE_TOKENS.accent;
  const brandFilter = Boolean(options?.brandFilter);

  return createTheme({
    ...(options?.headerColor
      ? {
          palette: {
            primary: { main: "#6C3CE9" },
            text: { primary: TABLE_TOKENS.ink, secondary: "#5C5870" },
          },
        }
      : {}),
    typography: {
      fontFamily: TABLE_TOKENS.font,
    },
    components: {
      MuiPaper: {
        styleOverrides: {
          root: {
            boxShadow: "none",
            backgroundImage: "none",
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: `1px solid ${TABLE_TOKENS.border}`,
            fontFamily: TABLE_TOKENS.font,
          },
          head: {
            backgroundColor: TABLE_TOKENS.headerBg,
            color: headerColor,
            fontWeight: 600,
            fontSize: "0.8125rem",
            letterSpacing: "0.02em",
            textTransform: "uppercase",
            whiteSpace: "nowrap",
            padding: "14px 16px",
          },
          body: {
            fontWeight: 400,
            fontSize: "0.875rem",
            color: TABLE_TOKENS.text,
            padding: "14px 16px",
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            transition: "background-color 160ms ease",
            "&:hover": {
              backgroundColor: `${TABLE_TOKENS.rowHover} !important`,
            },
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            backgroundColor: TABLE_TOKENS.headerBg,
          },
        },
      },
      MUIDataTableHeadCell: {
        styleOverrides: {
          root: {
            fontSize: "0.8125rem",
            color: headerColor,
            fontWeight: 600,
            backgroundColor: TABLE_TOKENS.headerBg,
            letterSpacing: "0.02em",
            textTransform: "uppercase",
          },
          sortAction: {
            fontSize: "0.8125rem",
            color: headerColor,
            fontWeight: 600,
            letterSpacing: "0.02em",
            textTransform: "uppercase",
            margin: "auto 0 !important",
          },
          sortActive: {
            fontSize: "0.8125rem",
            color: headerColor,
            fontWeight: 600,
            letterSpacing: "0.02em",
            textTransform: "uppercase",
          },
          contentWrapper: {
            justifyContent: "flex-start",
          },
        },
      },
      MuiTableSortLabel: {
        styleOverrides: {
          icon: {
            fill: headerColor,
            fontSize: "16px",
          },
        },
      },
      MUIDataTableToolbar: {
        styleOverrides: {
          root: {
            paddingLeft: "16px",
            paddingRight: "8px",
            minHeight: "64px",
          },
          filterPaper: brandFilter
            ? {
                width: "min(560px, calc(100vw - 32px))",
                maxWidth: "calc(100vw - 32px)",
                borderRadius: "22px",
                border: "1px solid #EAE8F2",
                boxShadow: "0 24px 50px -20px rgba(27, 19, 64, 0.35)",
                overflow: "visible",
                boxSizing: "border-box",
              }
            : {
                maxWidth: "52%",
                borderRadius: "12px",
                boxShadow:
                  "0 8px 24px rgba(27, 19, 64, 0.12), 0 2px 8px rgba(27, 19, 64, 0.08)",
                overflow: "visible",
              },
          icon: brandFilter
            ? {
                color: "#1B1340",
                "&:hover": { color: "#6C3CE9" },
              }
            : {},
          iconActive: brandFilter
            ? {
                color: "#6C3CE9",
              }
            : {},
        },
      },
      MUIDataTableSearch: {
        styleOverrides: {
          main: {
            alignItems: "center",
          },
        },
      },
      MUIDataTableFilterList: {
        styleOverrides: {
          chip: {
            display: "none",
          },
        },
      },
      MUIDataTableFilter: {
        styleOverrides: brandFilter
          ? {
              root: {
                width: "auto",
                minWidth: 0,
                maxWidth: "100%",
                boxSizing: "border-box",
                backgroundColor: "#fff",
                borderRadius: "22px",
                padding: "20px 16px 12px",
                "& .MuiGrid-container": {
                  width: "100% !important",
                  margin: "0 !important",
                },
              },
              header: {
                marginBottom: "4px",
                paddingRight: "28px",
              },
              title: {
                marginLeft: 0,
                color: "#1B1340",
                fontWeight: 700,
                fontSize: "16px",
                lineHeight: "22px",
                letterSpacing: "-0.02em",
              },
              resetLink: {
                minWidth: 0,
                height: 36,
                padding: "6px 12px",
                borderRadius: "12px",
                color: "#5C5870",
                fontWeight: 600,
                fontSize: "14px",
                letterSpacing: 0,
                textTransform: "none",
                "&:hover": { backgroundColor: "#F4F3F8" },
              },
              gridListTile: {
                margin: "0 !important",
                padding: "8px !important",
                boxSizing: "border-box",
                maxWidth: "50% !important",
                flexBasis: "50% !important",
              },
            }
          : {
              root: {
                minWidth: "480px",
                backgroundColor: "#fff",
                borderRadius: "12px",
                padding: "20px 24px 28px",
              },
              gridListTile: {
                marginTop: "8px !important",
                paddingTop: "4px !important",
                paddingBottom: "4px !important",
              },
            },
      },
      MuiTablePagination: {
        styleOverrides: {
          root: {
            borderTop: `1px solid ${TABLE_TOKENS.border}`,
            backgroundColor: "#FCFBFE",
          },
          selectLabel: {
            fontSize: "0.8125rem",
            color: TABLE_TOKENS.muted,
          },
          displayedRows: {
            fontSize: "0.8125rem",
            color: TABLE_TOKENS.muted,
          },
        },
      },
    },
  } as any);
};
